import { memo, useState } from "react";
import { Controller } from "react-hook-form";
import { MarketingConsentCheckbox } from "~/features/registration/components/MediasRegistrationBanner/MarketingConsentCheckbox";
import { TERMS_OF_USE_PAGE } from "~/utils/url/staticUrls";
import { Trans, useTranslation } from "react-i18next";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { Box, Button, Grid, Link, Stack, TextField, Typography } from "~/common/mui";
import {
  ErrorMessage,
  HelperLabelErrorMessage,
  useRegisterForFree,
} from "~/features/registration/hooks/useRegisterForFree";
import PersonIcon from "@mui/icons-material/Person";
import { validateEmail } from "~/utils/emailValidator";
import { getMediasVariant } from "~/features/navigation/components/TopBar/HeaderCircle";
import { SCHAEFFLER_LOGO } from "~/utils/url/staticResources";
import ResetSuccessContent from "~/features/auth/login/components/ResetSuccessContent";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { Overlay } from "~/common/components/Overlay/Overlay";
import {
  FORM_CONTAINER_WIDTH,
  FormContainer,
  FormGrid,
  LeftSection,
  LogoRow,
  OverlayHeaderStyled,
  RightImage,
  RightTextContainer,
  StyledMediasLogo,
  StyledOverlayContent,
  StyledStack,
} from "~/features/auth/login/components/loginPopupStyles";

interface RegisterContentProps {
  onLoginClick: () => void;
  onFullWidthChange?: (fullWidth: boolean) => void;
}

export const RegisterContent = ({ onLoginClick, onFullWidthChange }: RegisterContentProps) => {
  const { t } = useTranslation();
  const mediasVariant = getMediasVariant();

  const { errors, control, emailConfirmation, onSubmitHandler } = useRegisterForFree({
    onRegistrationSuccess: () => onFullWidthChange?.(true),
  });

  return (
    <>
      {emailConfirmation ? (
        <LeftSection>
          <ResetSuccessContent />
        </LeftSection>
      ) : (
        <LeftSection>
          <Grid container spacing={3} sx={{ width: "100%" }}>
            <FormGrid size={12}>
              <Stack sx={{ alignItems: "center", gap: 4, maxWidth: FORM_CONTAINER_WIDTH, width: "100%" }}>
                <StyledStack>
                  <LogoRow>
                    <StyledMediasLogo variant={mediasVariant} href={false} />
                    <Box
                      component="img"
                      sx={{ height: "12px", pl: 1 }}
                      src={SCHAEFFLER_LOGO}
                      alt={t("schaeffler.logo.alt.text")}
                    />
                  </LogoRow>

                  <Typography variant="headlineLarge" sx={{ color: "textSecondary.main", textAlign: "center" }}>
                    {t("create.free.account")}
                  </Typography>
                </StyledStack>

                <form onSubmit={onSubmitHandler} style={{ width: "100%" }}>
                  <FormContainer>
                    <Controller
                      name={"email"}
                      control={control}
                      render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          type="email"
                          autoComplete="email"
                          label={t("register.overlay.emailPlaceholder")}
                          error={!!error}
                          helperText={<HelperLabelErrorMessage error={error} />}
                          // without an explicit type/autoComplete, iOS Safari renders its own multi-line email suggestion UI over the field
                          inputProps={{
                            "data-test-id": "register-for-free-email",
                            inputMode: "email",
                            spellCheck: false,
                            autoCapitalize: "none",
                          }}
                          inputRef={ref}
                          sx={{
                            "& .MuiInputBase-input": {
                              minWidth: 0,
                              overflow: "hidden !important",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap !important",
                            },
                          }}
                          {...inputProps}
                        />
                      )}
                      rules={{
                        validate: validateEmail("error.invalidEmail", "error.invalidEmail"),
                      }}
                    />
                    <Stack sx={{ gap: 3 }}>
                      <Controller
                        name={"generalTerms"}
                        control={control}
                        rules={{ required: { value: true, message: "error.tos" } }}
                        render={({ field: { value, onChange } }) => (
                          <CheckboxWithLabel
                            data-test-id={"register-for-free__general-terms"}
                            checked={value}
                            onCheck={onChange}
                            centerCheckbox={false}
                            sx={{ pt: 0 }}
                          >
                            <Typography>
                              {t("page.registerFree.terms-part1")}
                              <a href={TERMS_OF_USE_PAGE}>{t("page.registerFree.terms-link")}</a>
                              {t("page.registerFree.terms-part2")}
                            </Typography>
                          </CheckboxWithLabel>
                        )}
                      />
                      <ErrorMessage error={errors.generalTerms} />

                      <Controller
                        name={"privacyPolicy"}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <MarketingConsentCheckbox
                            data-test-id={"register-for-free__privacy-policy"}
                            checked={value}
                            onCheck={onChange}
                            centerCheckbox={false}
                            sx={{ pt: 0 }}
                          />
                        )}
                      />
                    </Stack>

                    <Button
                      startIcon={<PersonIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
                      fullWidth={true}
                      data-test-id={"register-for-free__submit"}
                      onClick={onSubmitHandler}
                    >
                      {t("page.registerFree.register")}
                    </Button>
                  </FormContainer>
                </form>

                <Stack sx={{ flexDirection: "row", gap: 1 }}>
                  <Typography component={"span"} sx={{ color: "textSecondary.main" }}>
                    {t("register.overlay.alreadyAUser")}
                  </Typography>
                  <Link component="button" onClick={onLoginClick} sx={{ color: "inherit", fontWeight: "bold" }}>
                    {t("page.registerFree.already-link")}
                  </Link>
                </Stack>
                <Typography
                  variant="bodySmall"
                  sx={{ display: { phone: "block", "desktop-s": "none" }, textAlign: "center" }}
                >
                  <Trans i18nKey={"register.overlay.footerText"} />
                </Typography>
              </Stack>
            </FormGrid>
          </Grid>
        </LeftSection>
      )}
    </>
  );
};

const RegisterOverlay = ({
  closeRegisterOverlay,
  ...contentProps
}: RegisterContentProps & { closeRegisterOverlay: () => void }) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  const isTablet = screenSize <= useNumericScreenSize.tablet;
  const [fullWidthContent, setFullWidthContent] = useState(false);
  const onFullWidthChange = (fullWidth: boolean) => {
    setFullWidthContent(fullWidth);
    contentProps.onFullWidthChange?.(fullWidth);
  };

  return (
    <Overlay open={true}>
      <OverlayHeaderStyled onClose={closeRegisterOverlay} />
      <StyledOverlayContent>
        <Grid container spacing={3} sx={{ width: "100%" }}>
          <Grid
            size={isTablet || fullWidthContent ? 12 : 6}
            sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
          >
            <RegisterContent {...contentProps} onFullWidthChange={onFullWidthChange} />
          </Grid>
          {!isTablet && !fullWidthContent && (
            <Grid size={6}>
              <RightImage>
                <RightTextContainer>
                  <Typography variant="headlineLarge" sx={{ color: "textPrimary.main" }}>
                    {t("configure.linear.solution.heading")}
                  </Typography>
                  <Typography variant="titleLarge" sx={{ color: "textSecondary.main" }}>
                    {t("configure.linear.solution.subHeading")}
                  </Typography>
                </RightTextContainer>
              </RightImage>
            </Grid>
          )}
        </Grid>
      </StyledOverlayContent>
    </Overlay>
  );
};

export default memo(RegisterOverlay);
